import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Star, ShieldCheck, Zap, HeartHandshake, ChevronDown, Info, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ServiceRecommendationEngine } from "@/components/ServiceRecommendationEngine";
import { useLocation } from "@/lib/locations";
import { getLocalContent } from "@/lib/city-content";
import { useCalculatorState } from "@/hooks/use-calculator-state";

const BOOKING_URL = "/book";

export const Services = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { update } = useCalculatorState();
  const localContent = getLocalContent(location.name, location.regionSlug);
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.02 });

  const handleBookWithService = (service: 'standard' | 'deep' | 'move') => {
    update({ serviceType: service });
    navigate(`${BOOKING_URL}?view=custom-plan`);
  };

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-900/20" id="services">
      <div className="container" ref={sectionRef}>
        
        {/* HERO TOP */}
        <div className={cn("grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center mb-16 anim-fade-up", sectionVisible && "is-visible")}>
          <div>
            <div className="text-primary dark:text-primary font-bold tracking-widest uppercase text-xs mb-4">Beyond Cleaning</div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-foreground dark:text-white">
              Choose The Right Clean For Your Home
            </h2>
            <p className="text-lg text-muted-foreground dark:text-slate-300 max-w-2xl mb-4">
              Simple, premium cleaning options designed to help you book in under 60 seconds. All backed by our 200% satisfaction guarantee.
            </p>
            <p className="text-base text-muted-foreground dark:text-slate-400 max-w-2xl mb-8 border-l-4 border-primary pl-4">
              {localContent.localIssuesText}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="rounded-full px-8 text-base shadow-elegant">
                <a href={`${BOOKING_URL}?view=custom-plan`} aria-label="Get instant price for cleaning services">
                  <Zap className="mr-2 h-4 w-4 fill-primary/20" />
                  Get Instant Price 
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full px-8 text-base shadow-sm border-border dark:border-slate-700 dark:text-white dark:hover:bg-slate-800"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('open-recommendation-engine'));
                }}
              >
                <Sparkles className="mr-2 h-4 w-4 text-primary dark:text-primary" />
                Help Me Choose
              </Button>
            </div>
          </div>

          <div className="bg-gradient-to-br from-white to-blue-50 dark:from-slate-800 dark:to-slate-900 p-8 rounded-3xl shadow-elegant border border-blue-100 dark:border-slate-700">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl font-bold text-center flex flex-col items-center gap-2 shadow-sm border border-slate-100 dark:border-slate-700">
                <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
                <span className="text-sm dark:text-white">5-Star Rated</span>
              </div>
              <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl font-bold text-center flex flex-col items-center gap-2 shadow-sm border border-slate-100 dark:border-slate-700">
                <ShieldCheck className="h-6 w-6 text-primary dark:text-primary" />
                <span className="text-sm dark:text-white">Fully Insured</span>
              </div>
              <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl font-bold text-center flex flex-col items-center gap-2 shadow-sm border border-slate-100 dark:border-slate-700">
                <Zap className="h-6 w-6 text-orange-400 fill-orange-400" />
                <span className="text-sm dark:text-white">Fast Booking</span>
              </div>
              <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl font-bold text-center flex flex-col items-center gap-2 shadow-sm border border-slate-100 dark:border-slate-700">
                <HeartHandshake className="h-6 w-6 text-red-400 fill-red-400" />
                <span className="text-sm dark:text-white">200% Guarantee</span>
              </div>
            </div>
          </div>
        </div>


        {/* QUICK COMPARISON */}
        <div className={cn("mb-16 bg-white dark:bg-slate-800 border border-border dark:border-slate-700 rounded-3xl p-8 shadow-sm anim-fade-up", sectionVisible && "is-visible")}>
          <div className="text-primary dark:text-primary font-bold tracking-widest uppercase text-xs mb-2">Quick Comparison</div>
          <h3 className="text-2xl font-bold mb-6 dark:text-white">Which Package Is Right For You?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <button className="bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl text-left hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors group relative border border-transparent dark:border-slate-700">
                  <strong className="block text-foreground dark:text-white mb-1 flex items-center justify-between">
                    Standard
                    <Info className="h-3.5 w-3.5 text-muted-foreground dark:text-slate-400 group-hover:text-primary transition-colors" />
                  </strong>
                  <span className="text-sm text-muted-foreground dark:text-slate-400">Routine maintenance & refresh</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-5 rounded-2xl shadow-xl border-border dark:border-slate-700 bg-white dark:bg-slate-800">
                <div className="space-y-3">
                  <h4 className="font-bold text-primary dark:text-primary flex items-center gap-2">
                    <Zap className="h-4 w-4 fill-primary/20 dark:fill-primary/20" /> Standard Clean Includes:
                  </h4>
                  <ul className="grid grid-cols-1 gap-2 text-xs text-muted-foreground dark:text-slate-300">
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Kitchen surfaces & countertops</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Bathrooms (toilet, sink, shower)</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Dusting accessible surfaces</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Vacuuming & mopping floors</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Exterior of appliances</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Trash removal</li>
                  </ul>
                  <Button variant="link" className="p-0 h-auto text-xs font-bold text-primary dark:text-primary" onClick={() => handleBookWithService('standard')}>
                    Select Standard & Get Price →
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button className="bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl text-left hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors group relative border border-transparent dark:border-slate-700">
                  <strong className="block text-foreground dark:text-white mb-1 flex items-center justify-between">
                    Deep Clean
                    <Info className="h-3.5 w-3.5 text-muted-foreground dark:text-slate-400 group-hover:text-primary transition-colors" />
                  </strong>
                  <span className="text-sm text-muted-foreground dark:text-slate-400">First-time or overdue homes</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-5 rounded-2xl shadow-xl border-border dark:border-slate-700 bg-white dark:bg-slate-800">
                <div className="space-y-3">
                  <h4 className="font-bold text-primary dark:text-primary flex items-center gap-2">
                    <Zap className="h-4 w-4 fill-primary/20 dark:fill-primary/20" /> Deep Clean Includes:
                  </h4>
                  <p className="text-[11px] font-bold text-muted-foreground dark:text-slate-300 uppercase tracking-wider bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">Everything in Standard +</p>
                  <ul className="grid grid-cols-1 gap-2 text-xs text-muted-foreground dark:text-slate-300">
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Baseboards (hand-wiped)</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Door frames & light switches</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Detailed bathroom buildup removal</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Kitchen grease & grime removal</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Hard-to-reach areas</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Interior appliance exteriors</li>
                  </ul>
                  <Button variant="link" className="p-0 h-auto text-xs font-bold text-primary dark:text-primary" onClick={() => handleBookWithService('deep')}>
                    Select Deep & Get Price →
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button className="bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl text-left hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors group relative border border-transparent dark:border-slate-700">
                  <strong className="block text-foreground dark:text-white mb-1 flex items-center justify-between">
                    Move In/Out
                    <Info className="h-3.5 w-3.5 text-muted-foreground dark:text-slate-400 group-hover:text-primary transition-colors" />
                  </strong>
                  <span className="text-sm text-muted-foreground dark:text-slate-400">Empty home reset</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-5 rounded-2xl shadow-xl border-border dark:border-slate-700 bg-white dark:bg-slate-800">
                <div className="space-y-3">
                  <h4 className="font-bold text-primary dark:text-primary flex items-center gap-2">
                    <Zap className="h-4 w-4 fill-primary/20 dark:fill-primary/20" /> Move In/Out Includes:
                  </h4>
                  <p className="text-[11px] font-bold text-muted-foreground dark:text-slate-300 uppercase tracking-wider bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">Everything in Deep Clean +</p>
                  <ul className="grid grid-cols-1 gap-2 text-xs text-muted-foreground dark:text-slate-300">
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Inside empty cabinets & drawers</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Inside appliances (oven, fridge)</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Full bathroom & kitchen reset</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Dust & debris from empty spaces</li>
                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-primary dark:text-primary" /> Deposit-ready finish</li>
                  </ul>
                  <Button variant="link" className="p-0 h-auto text-xs font-bold text-primary dark:text-primary" onClick={() => handleBookWithService('move')}>
                    Select Move In/Out & Get Price →
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 p-4 rounded-2xl">
              <strong className="block text-primary dark:text-primary mb-1">Recurring</strong>
              <span className="text-sm text-muted-foreground dark:text-slate-300">Maximum savings & consistency</span>
            </div>
          </div>
        </div>

        {/* PACKAGES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          
          {/* STANDARD */}
          <div className={cn("bg-white dark:bg-slate-800 border border-border dark:border-slate-700 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col anim-fade-up", sectionVisible && "is-visible")}>
            <h3 className="text-2xl font-bold mb-2 dark:text-white">Standard Clean</h3>
            <p className="text-muted-foreground dark:text-slate-400 text-sm mb-6 min-h-[40px]">Routine upkeep & refreshes for your home.</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Kitchen + Baths</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Floors + Dusting</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Surface Wipe Down</li>
              <li className="flex items-center gap-3 text-sm dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Beds + Trash</li>
            </ul>
            <Button variant="outline" className="w-full rounded-xl border-border dark:border-slate-600 hover:bg-secondary dark:hover:bg-slate-700 dark:text-white" onClick={() => handleBookWithService('standard')}>
              <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-primary/40" />
              Get Instant Quote
            </Button>
            <Link to="/services/standard-clean" className="text-xs text-center text-muted-foreground dark:text-slate-400 hover:text-primary dark:hover:text-primary mt-4 block">Learn more about Standard Clean</Link>
          </div>

          {/* DEEP CLEAN */}
          <div className={cn("bg-white dark:bg-slate-800 border border-border dark:border-slate-700 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col relative anim-fade-up", sectionVisible && "is-visible")}>
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-slate-800 dark:bg-slate-900 text-white font-black text-xs sm:text-sm uppercase tracking-widest px-6 py-2 rounded-full shadow-xl ring-4 ring-background dark:ring-slate-800 whitespace-nowrap">
              Best For Initial Clean
            </div>
            <h3 className="text-2xl font-bold mb-2 mt-2 dark:text-white">Deep Clean</h3>
            <p className="text-muted-foreground dark:text-slate-400 text-sm mb-6 min-h-[40px]">First visit or overdue homes needing extra care.</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Everything in Standard</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Baseboards + Trim</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Build-Up Removal</li>
              <li className="flex items-center gap-3 text-sm dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Detailed Scrubbing</li>
              </ul>
              <Button variant="outline" className="w-full rounded-xl border-border dark:border-slate-600 hover:bg-secondary dark:hover:bg-slate-700 dark:text-white" onClick={() => handleBookWithService('deep')}>
                <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-primary/40" />
                Get Instant Quote
              </Button>
            <Link to="/services/deep-clean" className="text-xs text-center text-muted-foreground dark:text-slate-400 hover:text-primary dark:hover:text-primary mt-4 block">Learn more about Deep Clean</Link>
          </div>

          {/* MOVE IN/OUT */}
          <div className={cn("bg-white dark:bg-slate-800 border border-border dark:border-slate-700 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col anim-fade-up", sectionVisible && "is-visible")}>
            <h3 className="text-2xl font-bold mb-2 dark:text-white">Move In / Out</h3>
            <p className="text-muted-foreground dark:text-slate-400 text-sm mb-6 min-h-[40px]">Stress-free moving clean for empty homes.</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Inside Cabinets</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Oven + Fridge</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-border dark:border-slate-700 dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Empty Home Reset</li>
              <li className="flex items-center gap-3 text-sm dark:text-slate-200"><Check className="h-4 w-4 text-primary dark:text-primary shrink-0" /> Deposit Ready Finish</li>
            </ul>
            <Button variant="outline" className="w-full rounded-xl border-border dark:border-slate-600 hover:bg-secondary dark:hover:bg-slate-700 dark:text-white" onClick={() => handleBookWithService('move')}>
              <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-primary/40" />
              Get Instant Quote
            </Button>
            <Link to="/services/move-in-out" className="text-xs text-center text-muted-foreground dark:text-slate-400 hover:text-primary dark:hover:text-primary mt-4 block">Learn more about Move In / Out Clean</Link>
          </div>

          {/* RECURRING (FEATURED) */}
          <div className={cn("bg-primary text-primary-foreground rounded-3xl p-8 shadow-elegant hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col relative anim-fade-up", sectionVisible && "is-visible")}>
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground font-black text-xs sm:text-sm uppercase tracking-widest px-6 py-2 rounded-full shadow-xl shadow-accent/30 ring-4 ring-background whitespace-nowrap">
              SAVE 20-35%
            </div>
            <h3 className="text-2xl font-bold mb-2 mt-2">Recurring</h3>
            <p className="text-primary-foreground/80 text-sm mb-6 min-h-[40px]">Weekly / Biweekly / Monthly consistency.</p>
            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-primary-foreground/20"><Check className="h-4 w-4 text-accent shrink-0" /> Lower Rates & Discounts</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-primary-foreground/20"><Check className="h-4 w-4 text-accent shrink-0" /> Priority Scheduling</li>
              <li className="flex items-center gap-3 text-sm pb-3 border-b border-primary-foreground/20"><Check className="h-4 w-4 text-accent shrink-0" /> Consistently Clean Home</li>
              <li className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-accent shrink-0" /> Best Long-Term Value</li>
            </ul>
            <Button asChild className="w-full rounded-xl bg-background text-primary hover:bg-secondary shadow-lg btn-pulse">
              <a href={`${BOOKING_URL}?view=custom-plan`}>
                <Zap className="mr-2 h-4 w-4 fill-primary/20" />
                Start Saving 20-35%
              </a>
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full mt-4 text-xs text-center text-primary-foreground/80 hover:text-white transition-colors flex items-center justify-center gap-1">
                  View frequency discounts <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-4 border-border shadow-xl" side="top" align="center">
                <div className="text-[11px] font-bold text-center mb-3 text-primary tracking-wider uppercase">Frequency Discounts</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">Weekly</span>
                    <span className="font-bold text-primary text-xs">35% OFF</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">Bi-Weekly</span>
                    <span className="font-bold text-primary text-xs">30% OFF</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">3 Weeks</span>
                    <span className="font-bold text-primary text-xs">25% OFF</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-2 flex flex-col items-center justify-center text-center">
                    <span className="text-muted-foreground text-[11px] mb-0.5">Monthly</span>
                    <span className="font-bold text-primary text-xs">20% OFF</span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

        </div>

        {/* RECOMMENDATION ENGINE */}
        <ServiceRecommendationEngine />

        {/* SPECIALIZED SERVICES */}
        <div className={cn("bg-white dark:bg-slate-800 border border-border dark:border-slate-700 rounded-3xl p-10 shadow-sm anim-fade-up delay-600", sectionVisible && "is-visible")}>
          <div className="text-primary dark:text-primary font-bold tracking-widest uppercase text-xs mb-2">Specialized Services</div>
          <h3 className="text-3xl font-bold mb-4 dark:text-white">Need Something More Specific?</h3>
          <p className="text-muted-foreground dark:text-slate-400 mb-10 max-w-2xl">
            Beyond our core residential offerings, we provide specialized cleaning for unique situations.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-50 dark:bg-slate-900 border border-border dark:border-slate-700 rounded-2xl p-6 flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
              <h4 className="text-xl font-bold mb-1 dark:text-white">Post Construction</h4>
              <p className="text-sm text-muted-foreground dark:text-slate-400 mb-4">Dust, debris, and remodel cleanup</p>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm dark:text-slate-300"><Check className="h-3 w-3 text-primary dark:text-primary shrink-0" /> Fine Dust Removal</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-300"><Check className="h-3 w-3 text-primary dark:text-primary shrink-0" /> Surface Detailing</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-300"><Check className="h-3 w-3 text-primary dark:text-primary shrink-0" /> Floors + Fixtures</li>
              </ul>
              <Button asChild variant="outline" className="w-full rounded-xl border-border dark:border-slate-700 dark:text-white dark:hover:bg-slate-800">
                <a href="/services/post-construction">
                  <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-primary/40" />
                  Get Post Construction Price
                </a>
              </Button>
            </div>

            <div className="bg-slate-50 dark:bg-slate-900 border border-border dark:border-slate-700 rounded-2xl p-6 flex flex-col hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
              <h4 className="text-xl font-bold mb-1 dark:text-white">Office Cleaning</h4>
              <p className="text-sm text-muted-foreground dark:text-slate-400 mb-4">Reliable commercial maintenance</p>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm dark:text-slate-300"><Check className="h-3 w-3 text-primary dark:text-primary shrink-0" /> Desks + Common Areas</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-300"><Check className="h-3 w-3 text-primary dark:text-primary shrink-0" /> Restrooms + Kitchens</li>
                <li className="flex items-center gap-2 text-sm dark:text-slate-300"><Check className="h-3 w-3 text-primary dark:text-primary shrink-0" /> After-Hours Options</li>
              </ul>
              <Button asChild variant="outline" className="w-full rounded-xl border-border dark:border-slate-700 dark:text-white dark:hover:bg-slate-800">
                <a href="/services/office-cleaning">
                  <Zap className="mr-2 h-4 w-4 fill-primary/20 dark:fill-primary/40" />
                  Get Commercial Quote
                </a>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
